import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Upload, RefreshCcw, CheckCircle2 } from "lucide-react";

const USTADZ_META = {
  "erlan-iskandar":     { name: "Ustadz Erlan Iskandar",     title: "ST., M.Psi." },
  "yulian-purnama":     { name: "Ustadz Yulian Purnama",     title: "S.Kom." },
  "seno-aji-imanullah": { name: "Ustadz Seno Aji Imanullah", title: "S.S." },
  "ristiyan-ragil":     { name: "Ustadz Ristiyan Ragil P.",  title: "ST., MT." },
  "abu-yazid-nurdin":   { name: "Ustadz Abu Yazid Nurdin",   title: "Lc., M.Pd." },
  "muhammad-rezki":     { name: "Ustadz Muhammad Rezki Hr.", title: "M.Eng., Ph.D." },
};

const API = process.env.REACT_APP_BACKEND_URL;

export default function UstadzTab() {
  const { authHeaders } = useAuth();
  const [items, setItems] = React.useState([]);
  const [busy, setBusy] = React.useState(null);
  const [bumpKey, setBumpKey] = React.useState(0); // cache-bust image after upload

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/ustadz", { headers: authHeaders() });
      setItems(r.data);
    } catch {
      toast.error("Gagal memuat data ustadz");
    }
  }, [authHeaders]);

  React.useEffect(() => { load(); }, [load]);

  const onUpload = async (slug, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File terlalu besar (max 5 MB)");
      return;
    }
    setBusy(slug);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/admin/ustadz/${slug}/photo`, form, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`Foto ${USTADZ_META[slug]?.name || slug} berhasil diupload`);
      setBumpKey((k) => k + 1);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload gagal");
    } finally {
      setBusy(null);
    }
  };

  return (
    <TabsContent value="ustadz" data-testid="tab-ustadz-content">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-serif text-2xl text-primary">Foto Ustadz Pembimbing</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Upload / ganti foto ustadz. Format: JPG/PNG/WebP, max 5 MB. Foto otomatis dikonversi ke PNG.
            </p>
          </div>
          <button
            onClick={load}
            data-testid="ustadz-refresh"
            className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-border hover:bg-muted transition-colors"
          >
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((u) => {
            const meta = USTADZ_META[u.slug] || { name: u.slug, title: "" };
            const isBusy = busy === u.slug;
            return (
              <div key={u.slug} data-testid={`ustadz-row-${u.slug}`} className="border border-border rounded-lg p-4 flex gap-4">
                <div className="w-24 h-24 shrink-0 bg-muted rounded-lg overflow-hidden ring-1 ring-border">
                  {u.exists ? (
                    <img
                      src={`${API}${u.url}?v=${bumpKey}`}
                      alt={meta.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-xs text-muted-foreground">Belum ada</div>
                  )}
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0">
                      <h3 className="font-serif text-primary leading-tight truncate">{meta.name}</h3>
                      <p className="text-xs text-muted-foreground">{meta.title}</p>
                    </div>
                    {u.exists && <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-1" />}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                    {u.exists ? `${(u.size / 1024).toFixed(0)} KB` : "—"}
                  </p>
                  <label
                    data-testid={`ustadz-upload-${u.slug}`}
                    className={`mt-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold cursor-pointer transition-colors ${
                      isBusy
                        ? "bg-muted text-muted-foreground cursor-not-allowed"
                        : "bg-primary text-white hover:bg-primary/90"
                    }`}
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {isBusy ? "Mengupload..." : (u.exists ? "Ganti Foto" : "Upload Foto")}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      disabled={isBusy}
                      onChange={(e) => onUpload(u.slug, e.target.files?.[0])}
                    />
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-6 italic">
          Tip: gunakan foto berkualitas tinggi dengan wajah terlihat jelas. Foto square (1:1) akan tampil paling optimal.
        </p>
      </div>
    </TabsContent>
  );
}
